import { Disclosure } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { Container } from '../ui/Container';
import { cn } from '../../utils/cn';
import { useTranslation } from 'react-i18next';

type FAQItem = {
	question: string;
	answer: string;
};

export function FAQ() {
	const { t } = useTranslation();
	const faqs = t('faq.items', { returnObjects: true }) as FAQItem[];
	const partnerLinkUrl = t('faq.partnerLink.url');
	const partnerLinkText = t('faq.partnerLink.text');
	
	return (
		<section
			className="relative w-full py-16"
			style={{
				background:
					'linear-gradient(180deg, #e5e7eb 0%, #f3f4f6 100%)',
			}}
		>
			<svg
				className="absolute top-0 left-0 w-full h-16 z-0"
				viewBox="0 0 1440 64"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				preserveAspectRatio="none"
			>
				<path
					d="M0,32 C480,64 960,0 1440,32 L1440,0 L0,0 Z"
					fill="#f3f4f6"
				/>
			</svg>
			<div className="relative z-10">
				<Container>
					<div className="mx-auto max-w-2xl text-center">
						<h2 className="text-base font-semibold leading-7 text-warning">{t('nav.faq')}</h2>
						<p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
							{t('faq.subtitle')}
						</p>
					</div>

					<div className="mx-auto mt-16 max-w-3xl">
						<dl className="space-y-8">
							{faqs.map((faq, index) => (
								<Disclosure
									as="div"
									key={`${index}-${faq.question}`}
									className="border-b border-gray-200 pb-8"
								>
									{({ open }) => (
										<>
											<dt>
												<Disclosure.Button className="flex w-full items-start justify-between text-left">
													<span className="text-base font-semibold leading-7 text-gray-900">
														{faq.question}
													</span>
													<span className="ml-6 flex h-7 items-center">
														<ChevronDown
															className={cn(
																'h-6 w-6 transform text-warning transition-transform duration-200',
																open && 'rotate-180'
															)}
														/>
													</span>
												</Disclosure.Button>
											</dt>
											<Disclosure.Panel
												as="dd"
												className="mt-4 pr-12"
											>
												{index === 4 ? (
													<p className="text-base leading-7 text-gray-600">
														{t('faq.items.4.answerBefore')}{' '}
														<a
															href={partnerLinkUrl}
															className="text-warning underline"
															target="_blank"
															rel="noopener noreferrer"
														>
															{partnerLinkText}
														</a>
														{t('faq.items.4.answerAfter')}
													</p>
												) : (
													<p className="text-base leading-7 text-gray-600">{faq.answer}</p>
												)}
											</Disclosure.Panel>
										</>
									)}
								</Disclosure>
							))}
						</dl>
					</div>
				</Container>
			</div>
			<svg
				className="absolute bottom-0 left-0 w-full h-16 z-0"
				viewBox="0 0 1440 64"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				preserveAspectRatio="none"
			>
				<path
					d="M0,32 C480,0 960,64 1440,32 L1440,64 L0,64 Z"
					fill="#e5e7eb"
				/>
			</svg>
		</section>
	);
}